import { useState } from 'react';
import { apiBaseUrl } from '../authConfig.js';
import { Panel } from '../components/CommandCentreParts.js';
import { EmptyState } from '../components/NoData.js';
import { useApi, type GetToken } from '../lib/useApi.js';

interface Evidence {
  evidence_basis: 'MEASURED' | 'PRACTICE' | 'OBSERVATION';
  metric_name: string; metric_value: number | null; sample_size: number | null;
  query_text: string | null; source_reference: string | null;
}
interface Rec {
  recommendation_id: number; category: string; category_code: string; affects_spend: boolean;
  title: string; what_we_found: string; why_it_matters: string; recommended_action: string;
  expected_effect: string; confidence: 'HIGH' | 'MEDIUM' | 'LOW'; confidence_rationale: string | null;
  risk: 'HIGH' | 'MEDIUM' | 'LOW'; approval_required: boolean; status: string;
  generated_utc: string; decision: string | null; decided_by: string | null; decided_utc: string | null;
  evidence: Evidence[];
}
interface Response { recommendations: Rec[]; note: string; generated: number; generatedUtc: string }

const CONF: Record<string, string> = { HIGH: 'good', MEDIUM: 'warn', LOW: '' };
const BASIS_LABEL: Record<string, string> = {
  MEASURED: 'Measured', PRACTICE: 'Best practice', OBSERVATION: 'Observed',
};

function EvidenceList({ items }: { items: Evidence[] }): JSX.Element {
  return (
    <div style={{ marginTop: 14, borderTop: '1px solid var(--line)', paddingTop: 12 }}>
      {items.map((e, i) => (
        <div key={i} style={{ marginBottom: 10, fontSize: 13 }}>
          <span className={`pill ${e.evidence_basis === 'MEASURED' ? 'good' : ''}`}
                style={{ marginRight: 8 }}>{BASIS_LABEL[e.evidence_basis]}</span>
          <strong>{e.metric_name}</strong>
          {e.metric_value !== null ? <> — {Number(e.metric_value)}</> : null}
          {e.sample_size !== null ? <span style={{ color: 'var(--text-faint)' }}> (n={e.sample_size})</span> : null}
          {e.source_reference ? <div style={{ color: 'var(--text-muted)', marginTop: 3 }}>{e.source_reference}</div> : null}
          {e.query_text ? (
            <details style={{ marginTop: 4 }}>
              <summary style={{ cursor: 'pointer', color: 'var(--text-faint)', fontSize: 12 }}>Show the query</summary>
              <pre style={{ fontSize: 11, overflowX: 'auto', background: 'var(--tint)', padding: 10,
                            borderRadius: 6, marginTop: 6 }}>{e.query_text.trim()}</pre>
            </details>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export function Recommendations({ getToken }: { getToken: GetToken }): JSX.Element {
  const { data, error, refresh } = useApi<Response>('/api/recommendations', getToken);
  const [busy, setBusy] = useState<number | null>(null);

  const decide = async (id: number, decision: string): Promise<void> => {
    setBusy(id);
    try {
      const token = await getToken();
      await fetch(`${apiBaseUrl}/api/recommendations`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ recommendationId: id, decision }),
      });
      refresh();
    } finally { setBusy(null); }
  };

  if (error) return <EmptyState title="Could not load recommendations" tag="Error">{error}</EmptyState>;
  if (!data) return <EmptyState title="Loading…" tag="Please wait">Checking the rules against the data.</EmptyState>;

  const open = data.recommendations.filter((r) => r.status === 'PROPOSED');
  const closed = data.recommendations.filter((r) => r.status !== 'PROPOSED');

  return (
    <div className="stack">
      <div className="panel" style={{ background: 'var(--tint)' }}>
        <strong>Approving a recommendation records that you agree with it.</strong>{' '}
        <span style={{ color: 'var(--text-muted)' }}>{data.note}</span>
      </div>

      {open.length === 0 ? (
        <EmptyState title="Nothing to recommend" tag="No findings">
          No rule has met its threshold against the current data. That is a finding in itself —
          recommendations are not manufactured to fill the screen.
        </EmptyState>
      ) : null}

      {open.map((r) => (
        <Panel key={r.recommendation_id} title={r.title} subtitle={`${r.category}${r.affects_spend ? ' · affects spend' : ''}`}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
            <span className={`pill ${CONF[r.confidence] ?? ''}`}>{r.confidence} confidence</span>
            <span className={`pill ${r.risk === 'HIGH' ? 'bad' : r.risk === 'MEDIUM' ? 'warn' : ''}`}>{r.risk} risk</span>
            {r.approval_required ? <span className="pill warn">Needs your approval</span> : null}
          </div>

          <p><strong>What we found.</strong> {r.what_we_found}</p>
          <p><strong>Why it matters.</strong> {r.why_it_matters}</p>
          <p><strong>What to do.</strong> {r.recommended_action}</p>
          <p><strong>Expected effect.</strong> {r.expected_effect}</p>
          {r.confidence_rationale
            ? <p style={{ color: 'var(--text-muted)', fontSize: 13 }}><strong>Why that confidence.</strong> {r.confidence_rationale}</p>
            : null}

          <EvidenceList items={r.evidence} />

          <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
            {(['APPROVE', 'REJECT', 'REVIEW_LATER', 'IGNORE'] as const).map((d) => (
              <button key={d} className="btn-ghost" disabled={busy === r.recommendation_id}
                      onClick={() => { void decide(r.recommendation_id, d); }}>
                {d === 'REVIEW_LATER' ? 'Review later' : d.charAt(0) + d.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
        </Panel>
      ))}

      {closed.length > 0 ? (
        <Panel title="Decided" subtitle="Kept, not deleted. A rejected recommendation is evidence about how we decide.">
          <table className="data">
            <thead><tr><th>Recommendation</th><th>Decision</th><th>By</th><th>When</th></tr></thead>
            <tbody>
              {closed.map((r) => (
                <tr key={r.recommendation_id}>
                  <td>{r.title}</td>
                  <td>{r.decision ?? r.status}</td>
                  <td>{r.decided_by ?? '—'}</td>
                  <td>{r.decided_utc ? new Date(r.decided_utc).toLocaleString('en-GB') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      ) : null}
    </div>
  );
}
